/**
 * Calendar stage (§5d UI, mounted in the §5f shell) — the running-days pattern.
 *
 * THE PLANNER OWNS THE RUNNING DAYS. This screen edits the running days that
 * are CURRENTLY APPLIED to the modelling, and saves back to the set they came
 * from. It used to re-seed itself from thebigplan's calendar on every parent
 * re-render, which silently threw away edits in progress ("I saved all the
 * Fridays off and they've gone again"), and its only save path CREATED a new
 * set — so correcting a set meant inventing another one every time (Chris,
 * 2026-08-24). Now:
 *   - the grid loads the applied set and re-seeds only when the applied set
 *     actually changes;
 *   - Save changes updates that set in place, Save as new creates one;
 *   - unsaved work is visible and can be discarded deliberately;
 *   - bulk weekday edits do in one click what was being done date by date;
 *   - a date the published calendar does not cover is NOT assumed to run.
 *
 * W3: every date is painted in the RAILWAY'S OWN timetable colours (Red / Gold /
 * Pink / Blue + specials) straight from thebigplan's date→colour calendar, with
 * a YEAR view mirroring thebigplan's Season calendar.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import type { DayTypeRow, RunningDay } from '../types'
import type { BaselineDay } from '../baseline'

export interface SavedSetRow { id: number; name: string; detail: string; applied: boolean; isDefault: boolean }

export interface CalendarStageProps {
  season: string
  dayTypes: DayTypeRow[]
  /** thebigplan date→timetable-colour calendar (W3); empty = day-type fallback. */
  days?: BaselineDay[]
  /** The season's months, 'YYYY-MM'. THE CALENDAR'S UNIVERSE — a season being
   *  designed has no sales day-types, so the editable months come from here,
   *  not from sales (Chris, 2026-08-26). Falls back to the sales day-types. */
  monthKeys?: string[]
  /** Dates the model dropped as unprofitable — shown as their own category. */
  modelledNotRun?: string[]
  /** 'YYYY-MM' to open on. */
  initialMonth: string
  savedSets: SavedSetRow[]
  busy: boolean
  /** The running days currently applied to the modelling — what this screen edits. */
  appliedDays: RunningDay[] | null
  /** The saved set those days came from; null = the default standard timetable. */
  appliedSetId: number | null
  appliedSetName: string | null
  /** Pass an id to UPDATE that set in place; omit it to create a new one. */
  onSaveSet: (name: string, days: RunningDay[], id?: number) => void
  onApplySet: (id: number) => void
  onDeleteSet: (id: number) => void
  /** Mark this set the season's default (or clear it) — it then loads on open. */
  onSetDefault: (id: number, isDefault: boolean) => void
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const DOW = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
const pad = (n: number) => String(n).padStart(2, '0')
/** Mon=0 … Sun=6, from a 'YYYY-MM-DD' string (UTC noon dodges DST edges). */
const dowOf = (date: string) => (new Date(`${date}T12:00:00Z`).getUTCDay() + 6) % 7

// thebigplan's own pattern palette (tt_patterns.colour) + sensible specials.
const TT_COLOURS: Record<string, string> = {
  RED: '#B83A2B', YELLOW: '#A6771C', GOLD: '#A6771C', PINK: '#C25F94',
  BLUE: '#2B7A8C', GREEN: '#2E7D4F', PURPLE: '#6B4FA0', DINER: '#4E4459',
  GALA: '#8C5A1F', BLACK: '#333333',
}
const colourHex = (name: string): string => TT_COLOURS[name.trim().toUpperCase()] ?? '#6b6b5e'

interface TypeMeta { tag: string; cls: string; klass: string }
function typeMeta(dt: DayTypeRow): TypeMeta {
  if (dt.day_class === 'Sat') return { tag: 'Sat', cls: 'sat', klass: 'Sat' }
  if (dt.day_class === 'Sun') return { tag: 'Sun', cls: 'sun', klass: 'Sun' }
  if (dt.day_class === 'WkdyH' || dt.is_holiday) return { tag: 'Sch-hol', cls: 'schwkdy', klass: 'Sch-hol Wkdy' }
  return { tag: 'Wkdy', cls: 'wkdy', klass: 'Term Wkdy' }
}

export function CalendarStage(p: CalendarStageProps) {
  const byColour = useMemo(() => {
    const m = new Map<string, BaselineDay>()
    for (const d of p.days ?? []) m.set(d.date, d)
    return m
  }, [p.days])

  const byDate = useMemo(() => {
    const m = new Map<string, DayTypeRow>()
    for (const dt of p.dayTypes) m.set(dt.actual_date.slice(0, 10), dt)
    return m
  }, [p.dayTypes])

  const months = useMemo(() => {
    // THE SEASON DEFINES ITS MONTHS. A season being designed has no sales
    // day-types, so the editable months come from the season's own range
    // (monthKeys), never from sales — which left the calendar blank for every
    // speculative season (Chris, 2026-08-26). Sales day-types are the fallback
    // for an older payload that carries no season range.
    if (p.monthKeys && p.monthKeys.length) return [...new Set(p.monthKeys)].sort()
    const s = new Set<string>()
    for (const dt of p.dayTypes) s.add(dt.actual_date.slice(0, 7))
    return [...s].sort()
  }, [p.monthKeys, p.dayTypes])

  const modelledNotRunSet = useMemo(() => new Set(p.modelledNotRun ?? []), [p.modelledNotRun])

  /**
   * EVERY date of every season month — the editable universe.
   *
   * This used to be `p.dayTypes`, which comes from agg.ResearchDayType, a
   * table derived from SALES. A future date nobody has booked yet simply is
   * not in it: October 2026 had 18 of 31 rows, so thirteen dates could not be
   * clicked and, worse, could never be stored in a running set at all
   * (Chris, 2026-08-24: "I still can't edit the non-set dates"). Whether the
   * railway RUNS on a date is a forward planning decision and has nothing to
   * do with whether anyone has bought a ticket for it yet.
   */
  const allDates = useMemo(() => {
    const out: string[] = []
    for (const mk of months) {
      const [yy, mm] = mk.split('-').map(Number)
      if (!Number.isFinite(yy) || !Number.isFinite(mm)) continue
      const n = new Date(yy, mm, 0).getDate()
      for (let d = 1; d <= n; d++) out.push(`${yy}-${pad(mm)}-${pad(d)}`)
    }
    return out
  }, [months])
  const allDatesSet = useMemo(() => new Set(allDates), [allDates])

  /** The sales day-class where we have one, else the coarse day-of-week class
   *  — the same fallback the season model uses for an unclassified date. */
  const dayClassOf = (date: string): string => {
    const dt = byDate.get(date)
    if (dt) return dt.day_class
    const w = dowOf(date)
    return w === 5 ? 'Sat' : w === 6 ? 'Sun' : 'Wkdy'
  }
  /** A DayTypeRow for any date, synthesised when sales have not classified it. */
  const rowFor = (date: string): DayTypeRow =>
    byDate.get(date) ?? { actual_date: date, day_class: dayClassOf(date), is_holiday: false }

  /** One date's state as the planner has set it: does it run, and (optionally)
   *  which timetable has the planner assigned to it. An absent colour means
   *  "whatever thebigplan publishes for that date". */
  type DayState = { running: boolean; colour?: string }

  /** The running days as APPLIED (the saved truth this screen edits from). */
  const applied = useMemo(() => {
    const r: Record<string, DayState> = {}
    if (p.appliedDays?.length) {
      for (const d of p.appliedDays) r[d.date] = { running: d.running, ...(d.colour ? { colour: d.colour } : {}) }
      return r
    }
    // No set applied yet — the standard timetable. A date the published
    // calendar does not cover is OUTSIDE the season and does NOT run: assuming
    // it did invented phantom running days (law 8).
    for (const date of allDates) r[date] = { running: byColour.get(date)?.run ?? false }
    return r
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [p.appliedDays, allDates, byColour])

  /** Every timetable the railway publishes this season — the paint palette.
   *  Specials (gala/diner) are included: a planner may legitimately move one. */
  const palette = useMemo(() => {
    const seen = new Map<string, number>()
    for (const d of p.days ?? []) {
      const c = (d.colour ?? '').trim()
      if (!c || c.toUpperCase() === 'NONE') continue
      seen.set(c, (seen.get(c) ?? 0) + 1)
    }
    return [...seen.entries()].sort((a, b) => b[1] - a[1]).map(([c]) => c)
  }, [p.days])

  const [running, setRunning] = useState<Record<string, DayState>>(applied)
  const [name, setName] = useState('')
  /** The selected pill: a timetable colour to paint on, PUBLISHED to hand the
   *  date back to thebigplan's calendar, or OFF to close it. */
  const [paint, setPaint] = useState<string>('__off__')
  const [confirmDelete, setConfirmDelete] = useState<number | null>(null)
  const [bulkScope, setBulkScope] = useState<'month' | 'season'>('season')
  const [view, setView] = useState<'month' | 'year'>('year')

  // RE-SEED ONLY WHEN THE APPLIED SET REALLY CHANGES. Keying this on the
  // dayTypes/days array identities (as it was) meant any parent re-render
  // wiped the planner's edits mid-flow.
  const seedKey = `${p.season}|${p.appliedSetId ?? 'default'}|${p.appliedDays?.length ?? 0}|${Object.values(applied).filter((d) => d.running).length}|${Object.values(applied).filter((d) => d.colour).length}`
  const seededRef = useRef<string | null>(null)
  useEffect(() => {
    if (seededRef.current === seedKey) return
    seededRef.current = seedKey
    setRunning(applied)
    setName('')
  }, [seedKey, applied])

  const [ym, setYm] = useState(() => (months.includes(p.initialMonth) ? p.initialMonth : months[0] ?? p.initialMonth))
  useEffect(() => { if (!months.includes(ym) && months.length) setYm(months[0]) }, [months, ym])

  const [yr, mo] = ym.split('-').map(Number)
  const idx = months.indexOf(ym)
  const gotoMonth = (delta: number) => { const n = idx + delta; if (n >= 0 && n < months.length) setYm(months[n]) }

  const cellsFor = (y: number, m: number): ({ day: number; date: string } | null)[] => {
    const cells: ({ day: number; date: string } | null)[] = []
    const first = new Date(y, m - 1, 1)
    const lead = (first.getDay() + 6) % 7 // Mon=0
    const daysInMonth = new Date(y, m, 0).getDate()
    for (let i = 0; i < lead; i++) cells.push(null)
    for (let d = 1; d <= daysInMonth; d++) cells.push({ day: d, date: `${y}-${pad(m)}-${pad(d)}` })
    while (cells.length % 7 !== 0) cells.push(null)
    return cells
  }
  const cells = Number.isFinite(yr) && Number.isFinite(mo) ? cellsFor(yr, mo) : []

  const isRun = (d: string) => running[d]?.running ?? false
  /** The timetable a date actually works: the planner's override first, then
   *  thebigplan's published calendar. */
  const colourOf = (d: string): string => running[d]?.colour ?? byColour.get(d)?.colour ?? ''
  const isOverridden = (d: string) => !!running[d]?.colour

  const paintDate = (date: string) => {
    if (!allDatesSet.has(date)) return
    setRunning((r) => ({ ...r, [date]: applyPaintWith(r, date) }))
  }
  /** The selected pill applied to one date, against a specific map — bulk
   *  edits work off the pending map so a whole column lands in one update. */
  const applyPaintWith = (map: Record<string, DayState>, date: string): DayState => {
    if (paint === '__off__') return { ...map[date], running: false }
    if (paint === '__published__') return { running: byColour.get(date)?.run ?? false }
    return { running: true, colour: paint }
  }

  /** Every date this edit may touch — the visible month, or the whole season. */
  const scopeDates = (): string[] => {
    if (bulkScope === 'month') return cells.filter(Boolean).map((c) => c!.date).filter((d) => allDatesSet.has(d))
    return allDates
  }
  const paintWeekday = (dow: number) => {
    const targets = scopeDates().filter((d) => dowOf(d) === dow)
    if (targets.length === 0) return
    setRunning((r) => { const next = { ...r }; for (const d of targets) next[d] = applyPaintWith(r, d); return next })
  }
  const paintAll = () => {
    const targets = scopeDates()
    setRunning((r) => { const next = { ...r }; for (const d of targets) next[d] = applyPaintWith(r, d); return next })
  }
  /** Put the scope back to what the published calendar says — clears overrides. */
  const resetScopeToCalendar = () => {
    const targets = scopeDates()
    setRunning((r) => {
      const next = { ...r }
      for (const d of targets) next[d] = { running: byColour.get(d)?.run ?? false }
      return next
    })
  }

  // ---- counts for the visible month ----
  const monthDates = cells.filter(Boolean).map((c) => c!.date).filter((d) => allDatesSet.has(d))
  const count = (pred: (m: TypeMeta) => boolean) => monthDates.filter((d) => isRun(d) && pred(typeMeta(rowFor(d)))).length
  const operating = monthDates.filter(isRun).length
  const outsideCalendar = monthDates.filter((d) => !byColour.has(d)).length
  const seasonOperating = allDates.filter(isRun).length

  // Colours present this month, for the legend.
  const monthColours = [...new Set(monthDates.map((d) => byColour.get(d)?.colour).filter((c): c is string => !!c))]

  // ---- unsaved-work tracking ----
  const changed = useMemo(() => {
    const keys = new Set([...Object.keys(applied), ...Object.keys(running)])
    let n = 0
    for (const k of keys) {
      const a = applied[k], b = running[k]
      if ((a?.running ?? false) !== (b?.running ?? false) || (a?.colour ?? '') !== (b?.colour ?? '')) n += 1
    }
    return n
  }, [applied, running])

  const daysPayload = (): RunningDay[] => allDates.map((date) => {
    const col = running[date]?.colour
    const mnr = isRun(date) && modelledNotRunSet.has(date)
    return { date, day_class: dayClassOf(date), running: isRun(date), ...(col ? { colour: col } : {}), ...(mnr ? { modelledNotRun: true } : {}) }
  })
  const saveChanges = () => { if (p.appliedSetId != null) p.onSaveSet(p.appliedSetName ?? 'Running set', daysPayload(), p.appliedSetId) }
  const saveAsNew = () => p.onSaveSet(name.trim() || `${p.season} running days`, daysPayload())

  const dayCell = (c: { day: number; date: string } | null, i: number) => {
    if (!c) return <div key={i} className="sh-cal-cell sh-cal-cell--empty" />
    const meta = typeMeta(rowFor(c.date))
    const run = isRun(c.date)
    const colour = colourOf(c.date)
    const known = byColour.has(c.date)
    const mine = isOverridden(c.date)
    const painted = run && !!colour
    // A day the planner asked to run but the model dropped as unprofitable.
    const mnr = run && modelledNotRunSet.has(c.date)
    const paintLabel = paint === '__off__' ? 'close it' : paint === '__published__' ? 'hand it back to the published calendar' : `give it the ${paint} timetable`
    return (
      <button
        key={i} type="button" onClick={() => paintDate(c.date)}
        title={mnr
          ? `${meta.klass} — you asked to run this day, but the model found no train that pays here. Modelled NOT run. Click to ${paintLabel}.`
          : `${colour ? `${colour} timetable${mine ? ' (you assigned this)' : ''} · ` : known ? '' : 'outside the published calendar · '}${meta.klass}${run ? '' : ' — not running'} · click to ${paintLabel}`}
        className={`sh-cal-cell ${run ? (colour ? '' : `sh-cal-cell--${meta.cls}`) : 'sh-cal-cell--off'}`}
        style={mnr
          ? { background: '#f6ecdd', boxShadow: 'inset 0 0 0 2px rgba(178,59,46,0.55)', borderLeft: '3px solid #b23b2e', opacity: 0.95 }
          : painted
            ? { background: `${colourHex(colour)}22`, boxShadow: `inset 0 0 0 ${mine ? 2 : 1}px ${colourHex(colour)}${mine ? 'aa' : '55'}`, borderLeft: `3px solid ${colourHex(colour)}` }
            : !known ? { borderStyle: 'dashed' } : undefined}
      >
        <span className="sh-cal-num">{c.day}</span>
        <span className="sh-cal-tag" style={mnr ? { color: '#b23b2e', fontWeight: 700 } : painted ? { color: colourHex(colour), fontWeight: 700 } : undefined}>
          {mnr ? "won't run" : run ? (colour ? `${colour}${mine ? ' *' : ''}` : meta.tag) : 'off'}
        </span>
      </button>
    )
  }

  const btn = (label: string, onClick: () => void, title: string) => (
    <button type="button" onClick={onClick} title={title}
      style={{ border: '1px solid #ddd6c4', background: '#fffdf6', borderRadius: 5, padding: '3px 0', fontSize: '0.72rem', cursor: 'pointer', width: '100%' }}>
      {label}
    </button>
  )

  return (
    <div className="sh-grid2">
      <section className="sh-card sh-card--pad">
        <div className="sh-cal-head">
          <span className="sh-cal-title">{view === 'year' ? p.season : Number.isFinite(mo) ? `${MONTHS[mo - 1]} ${yr}` : 'No calendar'}</span>
          <div className="sh-cal-nav" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <button type="button" className="sh-cal-navbtn" style={{ width: 'auto', padding: '0 10px' }} onClick={() => setView(view === 'month' ? 'year' : 'month')}>
              {view === 'month' ? 'Year view' : 'Month view'}
            </button>
            {view === 'month' && <>
              <button type="button" className="sh-cal-navbtn" disabled={idx <= 0} onClick={() => gotoMonth(-1)}>‹</button>
              <button type="button" className="sh-cal-navbtn" disabled={idx < 0 || idx >= months.length - 1} onClick={() => gotoMonth(1)}>›</button>
            </>}
          </div>
        </div>
        {view === 'month' && (
          <>
            <div style={{ margin: '4px 0 10px' }}>
              <div className="sh-section-label" style={{ marginBottom: 5 }}>Assign this timetable when I click a day</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                {palette.map((c) => {
                  const on = paint === c
                  return (
                    <button key={c} type="button" onClick={() => setPaint(c)} title={`Click days to give them the ${c} timetable`}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 5, borderRadius: 999, cursor: 'pointer',
                        padding: '3px 10px', fontSize: '0.73rem', fontWeight: on ? 700 : 400,
                        border: `1px solid ${colourHex(c)}`, background: on ? colourHex(c) : '#fffdf6', color: on ? '#fff' : '#1d1d1b' }}>
                      <span style={{ width: 8, height: 8, borderRadius: 2, background: on ? '#fff' : colourHex(c) }} />
                      {c}
                    </button>
                  )
                })}
                {([['__off__', 'Not running'], ['__published__', 'As published']] as const).map(([key, label]) => {
                  const on = paint === key
                  return (
                    <button key={key} type="button" onClick={() => setPaint(key)}
                      title={key === '__off__' ? 'Click days to close them' : "Click days to hand them back to thebigplan's published calendar"}
                      style={{ borderRadius: 999, cursor: 'pointer', padding: '3px 10px', fontSize: '0.73rem', fontWeight: on ? 700 : 400,
                        border: '1px solid #9b968a', background: on ? '#4a463d' : '#fffdf6', color: on ? '#fff' : '#4a463d' }}>
                      {label}
                    </button>
                  )
                })}
              </div>
            </div>
            <div className="sh-cal-grid">
              {DOW.map((d, i) => (
                <button key={d} type="button" className="sh-cal-dow" title={`Apply the selected timetable to every ${d} in ${MONTHS[mo - 1]}`}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', font: 'inherit', color: 'inherit' }}
                  onClick={() => {
                    const targets = monthDates.filter((x) => dowOf(x) === i)
                    setRunning((r) => { const next = { ...r }; for (const t of targets) next[t] = applyPaintWith(r, t); return next })
                  }}>{d}</button>
              ))}
              {cells.map(dayCell)}
            </div>
            <div className="sh-cal-legend">
              {monthColours.map((c) => (
                <span key={c}><span className="sh-swatch" style={{ background: colourHex(c) }} /> {c} timetable</span>
              ))}
              {monthColours.length === 0 && <>
                <span><span className="sh-swatch sh-swatch--sat" /> Sat (peak)</span>
                <span><span className="sh-swatch sh-swatch--sun" /> Sun</span>
                <span><span className="sh-swatch sh-swatch--sch" /> Sch-hol Wkdy</span>
                <span><span className="sh-swatch sh-swatch--wkdy" /> Term Wkdy</span>
              </>}
              <span><span className="sh-swatch sh-swatch--off" /> Not running</span>
              {modelledNotRunSet.size > 0 && (
                <span><span className="sh-swatch" style={{ background: '#dca79c', boxShadow: 'inset 0 0 0 1.5px #b23b2e' }} /> Modelled — won&rsquo;t run</span>
              )}
            </div>
            <p className="stu-note" style={{ marginTop: 6 }}>
              Pick a timetable above, then click any date to give it that timetable — or pick <strong>Not running</strong> to close
              days, or <strong>As published</strong> to hand them back to thebigplan. Click a weekday heading to apply your choice to
              that whole column. A day you have assigned yourself is marked <strong>*</strong> and outlined more strongly.
              {outsideCalendar > 0 ? ` ${outsideCalendar} date${outsideCalendar === 1 ? '' : 's'} this month sit outside the published calendar (shown dashed) — they carry no timetable until you give them one.` : ''}
            </p>
          </>
        )}
        {view === 'year' && (
          // thebigplan's Season-calendar layout: a card per month, every date a
          // small square in its timetable colour. Click a month to edit it.
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: 12 }}>
            {months.map((m) => {
              const [y2, m2] = m.split('-').map(Number)
              const mini = cellsFor(y2, m2)
              const runN = mini.filter((c) => c && allDatesSet.has(c.date) && isRun(c.date)).length
              return (
                <button key={m} type="button" onClick={() => { setYm(m); setView('month') }}
                  style={{ border: '1px solid #ddd6c4', borderRadius: 8, padding: 8, background: '#fffdf6', cursor: 'pointer', textAlign: 'left' }}
                  title={`Open ${MONTHS[m2 - 1]} ${y2}`}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
                    <span style={{ fontWeight: 700, fontSize: '0.78rem' }}>{MONTHS[m2 - 1]} {y2}</span>
                    <span style={{ fontSize: '0.68rem', color: '#6b6b6b' }}>{runN} running</span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
                    {mini.map((c, i) => {
                      if (!c) return <span key={i} style={{ height: 14 }} />
                      const known = allDatesSet.has(c.date)
                      const colour = colourOf(c.date)
                      const run = known ? isRun(c.date) : false
                      const mnr = known && run && modelledNotRunSet.has(c.date)
                      const bg = !known ? 'transparent' : !run ? '#e7e2d2' : mnr ? '#dca79c' : colour ? colourHex(colour) : '#b9b39e'
                      return <span key={i} title={`${c.date}${colour ? ` · ${colour}` : ''}${mnr ? ' · modelled NOT run (no train pays)' : known && !run ? ' · off' : ''}`}
                        style={{ height: 14, borderRadius: 2, background: bg, opacity: run ? 1 : 0.7, boxShadow: mnr ? 'inset 0 0 0 1.5px #b23b2e' : undefined }} />
                    })}
                  </div>
                </button>
              )
            })}
          </div>
        )}
      </section>

      <div className="sh-stack">
        <section className="sh-card sh-card--pad">
          <div className="sh-section-label">Bulk edit</div>
          <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
            {(['season', 'month'] as const).map((s) => (
              <button key={s} type="button" onClick={() => setBulkScope(s)}
                style={{ flex: 1, border: '1px solid #ddd6c4', borderRadius: 5, padding: '4px 0', fontSize: '0.72rem', cursor: 'pointer',
                  background: bulkScope === s ? '#0b3d2e' : '#fffdf6', color: bulkScope === s ? '#fff' : 'inherit', fontWeight: bulkScope === s ? 700 : 400 }}>
                {s === 'season' ? 'Whole season' : `${Number.isFinite(mo) ? MONTHS[mo - 1] : 'This month'} only`}
              </button>
            ))}
          </div>
          <p className="stu-note" style={{ margin: '0 0 6px' }}>
            Applies the timetable selected above — {paint === '__off__' ? 'Not running' : paint === '__published__' ? 'As published' : paint}.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 3, alignItems: 'center' }}>
            {DOW.map((d) => <div key={d} style={{ fontSize: '0.68rem', textAlign: 'center', color: '#6b6b6b', fontWeight: 700 }}>{d[0]}</div>)}
            {DOW.map((d, i) => <div key={`w${d}`}>{btn('set', () => paintWeekday(i), `Apply to every ${d} — ${bulkScope === 'season' ? 'whole season' : 'this month'}`)}</div>)}
          </div>
          <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
            {btn('Apply to every day', paintAll, 'Apply the selected timetable to every date in scope')}
            {btn('Back to published', resetScopeToCalendar, "Reset the scope to thebigplan's published calendar")}
          </div>
        </section>

        <section className="sh-card sh-card--pad">
          <div className="sh-section-label">Running days</div>
          <div className="sh-kv"><span className="sh-kv-key">This season</span><strong>{seasonOperating} of {allDates.length}</strong></div>
          <div className="sh-kv"><span className="sh-kv-key">{Number.isFinite(mo) ? `${MONTHS[mo - 1]} ${yr}` : 'This month'}</span><strong>{operating} of {monthDates.length}</strong></div>
          <div className="sh-kv"><span className="sh-kv-key">Saturdays (peak)</span><strong>{count((m) => m.cls === 'sat')}</strong></div>
          <div className="sh-kv"><span className="sh-kv-key">Sundays</span><strong>{count((m) => m.cls === 'sun')}</strong></div>
          <div className="sh-kv"><span className="sh-kv-key">School-hol weekdays</span><strong>{count((m) => m.cls === 'schwkdy')}</strong></div>
          <div className="sh-kv"><span className="sh-kv-key">Term weekdays</span><strong>{count((m) => m.cls === 'wkdy')}</strong></div>
          {(() => {
            const mnr = allDates.filter((d) => isRun(d) && modelledNotRunSet.has(d)).length
            return mnr > 0 ? (
              <div className="sh-kv"><span className="sh-kv-key" style={{ color: '#b23b2e' }}>Modelled — won&rsquo;t run</span><strong style={{ color: '#b23b2e' }}>{mnr}</strong></div>
            ) : null
          })()}
        </section>

        <section className="sh-save-card">
          <div className="sh-save-title">
            {p.appliedSetId != null ? `Editing “${p.appliedSetName ?? 'Running set'}”` : 'Editing the standard timetable'}
          </div>
          <p className="sh-save-note">
            {changed > 0
              ? `${changed} day${changed === 1 ? '' : 's'} changed and not yet saved. The modelling still uses the saved version until you save.`
              : 'No unsaved changes. The modelling is using exactly what you see here.'}
          </p>
          {p.appliedSetId != null && (
            <button type="button" className="sh-save-btn" disabled={p.busy || changed === 0} onClick={saveChanges}>
              {p.busy ? 'Saving…' : `Save changes to “${p.appliedSetName ?? 'this set'}”`}
            </button>
          )}
          <input className="sh-save-input" type="text" placeholder="Name a new set, e.g. Sat–Wed early season" value={name} onChange={(e) => setName(e.target.value)} style={{ marginTop: 8 }} />
          <button type="button" className="sh-save-btn" disabled={p.busy || allDates.length === 0} onClick={saveAsNew}
            style={p.appliedSetId != null ? { background: 'transparent', color: '#0b3d2e', border: '1px solid #0b3d2e' } : undefined}>
            {p.busy ? 'Saving…' : 'Save as a new set'}
          </button>
          {changed > 0 && (
            <button type="button" className="sh-save-btn" onClick={() => setRunning(applied)}
              style={{ background: 'transparent', color: '#6b6b6b', border: '1px solid #ddd6c4', marginTop: 6 }}>
              Discard my changes
            </button>
          )}
        </section>

        {p.savedSets.length > 0 && (
          <section className="sh-card sh-card--pad">
            <div className="sh-section-label">Saved sets</div>
            {p.savedSets.map((s) => (
              <div className="sh-saved-set" key={s.id}>
                <div>
                  <div className="sh-saved-set-name">
                    {s.name}
                    {s.isDefault && <span className="sh-set-action sh-set-action--applied" style={{ marginLeft: 6 }} title="Loads automatically on open">★ Default</span>}
                  </div>
                  <div className="sh-saved-set-detail">{s.detail}</div>
                </div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', whiteSpace: 'nowrap' }}>
                  <button type="button" className="sh-set-action"
                    style={s.isDefault ? { color: '#0b3d2e', fontWeight: 700 } : undefined}
                    title={s.isDefault ? 'This set loads by default — click to clear' : 'Make this the default set that loads automatically on open'}
                    onClick={() => p.onSetDefault(s.id, !s.isDefault)}>
                    {s.isDefault ? '★ Default' : 'Set default'}
                  </button>
                  {s.applied
                    ? <span className="sh-set-action sh-set-action--applied">Applied</span>
                    : <button type="button" className="sh-set-action sh-set-action--apply" onClick={() => p.onApplySet(s.id)}>Load</button>}
                  {confirmDelete === s.id ? (
                    <>
                      <button type="button" className="sh-set-action" style={{ color: '#b23b2e', fontWeight: 700 }} onClick={() => { p.onDeleteSet(s.id); setConfirmDelete(null) }}>Delete for good</button>
                      <button type="button" className="sh-set-action" onClick={() => setConfirmDelete(null)}>Keep</button>
                    </>
                  ) : (
                    <button type="button" className="sh-set-action" title="Delete this saved set" onClick={() => setConfirmDelete(s.id)}>Delete</button>
                  )}
                </div>
              </div>
            ))}
          </section>
        )}
      </div>
    </div>
  )
}
